/**
 * Which table a Save Record / Delete Record action points at (KOA-18147).
 *
 * Both groups resolved the saved selection with one `find(id matches || name matches)`. When two
 * datasources hold a table of the same name, the name half matched whichever datasource is listed
 * first — and external tables carry no id, so for them the name half was all there was. The group
 * then showed and wrote the first datasource's columns, and picking the other table was refused as
 * "the same table" by that same name comparison.
 */

/** A table as Save / Delete Record list it: one datasource's schema entry, plus that datasource. */
export interface IManagedDbTable {
  id?: string;
  key: string;
  title: string;
  schema: string;
  dsId: string;
}

/** What a Save / Delete Record action keeps of the table it writes to. */
export interface IManagedDbTableSelection {
  selectedTableId?: string;
  selectedTableName?: string;
  selectedTableSchema?: string;
  selectedDatasourceId?: string;
}

/** The group's own name comparison, kept exactly as each group always compared. */
export type ManagedDbTableNameMatcher = (table: IManagedDbTable, selection: IManagedDbTableSelection) => boolean;

/**
 * The selected table: by its saved id first; otherwise by name, inside the saved datasource before
 * any other. A selection saved without a datasource id falls back to the first name match, as before.
 */
export function findSelectedManagedDbTable<T extends IManagedDbTable>(
  tables: readonly T[],
  selection: IManagedDbTableSelection | undefined,
  isNameMatch: ManagedDbTableNameMatcher
): T | undefined {
  if (!selection) return undefined;

  const selectedTableId: string | undefined = selection.selectedTableId?.toLowerCase();
  if (selectedTableId) {
    const byId: T | undefined = tables.find((table: T): boolean => table.id?.toLowerCase() === selectedTableId);
    if (byId) return byId;
  }

  const byName: T[] = tables.filter((table: T): boolean => isNameMatch(table, selection));
  return byName.find((table: T): boolean => table.dsId === selection.selectedDatasourceId) ?? byName[0];
}

/** Whether picking `pickedTable` would only re-select the table that is already selected. */
export function isSelectedManagedDbTable<T extends IManagedDbTable>(
  tables: readonly T[],
  selection: IManagedDbTableSelection | undefined,
  pickedTable: T,
  isNameMatch: ManagedDbTableNameMatcher
): boolean {
  return findSelectedManagedDbTable(tables, selection, isNameMatch)?.key === pickedTable.key;
}
